import { StrategyBuilderFunction, EMPTY_STRATEGY } from './types';
import { addLongCall, addShortCall } from './addOption';
import {
  getDefaultExpiration,
  findClosestStrikePrice,
  getStrikesAtExpiration,
  expirationToDate,
  getPremium,
  getStrategyLegStrikeDirection,
  getStrikeDistance,
} from './utils';

export const longCallCondor: StrategyBuilderFunction = ({
  chain,
  spot,
  expirationDate,
  expirationKey,
  strikeDirection,
  contractMultiplier = 1,
}) => {
  const strikesAtExpiration = expirationKey
    ? getStrikesAtExpiration(chain, expirationKey)
    : getDefaultExpiration(chain);

  if (
    !strikesAtExpiration ||
    !strikesAtExpiration.e ||
    !strikesAtExpiration.ey ||
    !strikesAtExpiration.em ||
    !strikesAtExpiration.ed
  ) {
    return EMPTY_STRATEGY;
  }

  const expirationDateForOptions =
    expirationDate ||
    expirationToDate(strikesAtExpiration.ey, strikesAtExpiration.em, strikesAtExpiration.ed);
  const expirationKeyForOptions = expirationKey || strikesAtExpiration.e;

  // Define strike prices
  const lowestStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: -2 }),
  ); // Buying 1 ITM call
  const lowerMiddleStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: -1 }),
  ); // Selling 1 ITM or ATM call
  const upperMiddleStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: 1 }),
  ); // Selling 1 OTM call
  const highestStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: 2 }),
  ); // Buying 1 OTM call

  return {
    optionPositions: [
      // Buy 1 ITM call option at the lowest strike
      addLongCall({
        strike: lowestStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[lowestStrike]?.c?.a || 0,
          bid: strikesAtExpiration?.c?.[lowestStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Sell 1 ITM or ATM call option at the lower middle strike
      addShortCall({
        strike: lowerMiddleStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[lowerMiddleStrike]?.c?.a || 0,
          bid: strikesAtExpiration?.c?.[lowerMiddleStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Sell 1 OTM call option at the upper middle strike
      addShortCall({
        strike: upperMiddleStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[upperMiddleStrike]?.c?.a || 0,
          bid: strikesAtExpiration?.c?.[upperMiddleStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Buy 1 OTM call option at the highest strike
      addLongCall({
        strike: highestStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[highestStrike]?.c?.a || 0,
          bid: strikesAtExpiration?.c?.[highestStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
    ],
    stockPositions: [],
  };
};
